// ===================================================
// GERAÇÃO DE PDF COM TEXTO REAL (sem rasterização)
// ===================================================
// Substitui o pipeline anterior (html2canvas + jsPDF.addImage), que produzia PDFs
// 100% imagem, sem nenhuma camada de texto real. Isso fazia leitores como Chrome/
// Edge acionarem OCR automático ao tentar selecionar/copiar o conteúdo, gerando os
// erros típicos de reconhecimento óptico relatados (ex.: "l"→"t", palavras coladas
// como "materialle"). Este renderer desenha texto real via jsPDF a partir do mesmo
// HTML semântico já usado nas propostas/contratos, sem depender de captura de tela.
// A fonte padrão do PDF (Helvetica/WinAnsiEncoding) já cobre toda a acentuação do
// português — não é preciso embutir uma fonte TTF à parte.
async function _carregarJsPDFTexto(){
  const carregarScript=src=>new Promise((resolve,reject)=>{
    const s=document.createElement('script');
    s.src=src;s.onload=resolve;s.onerror=()=>reject(new Error('Falha ao carregar '+src));
    document.head.appendChild(s);
  });
  if(!window.jspdf?.jsPDF)await carregarScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/4.2.1/jspdf.umd.min.js');
  if(!window.jspdf.jsPDF.API.autoTable)await carregarScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/5.0.8/jspdf.plugin.autotable.min.js');
}

// Poucos glifos decorativos (emoji, seta de hierarquia) ficam fora da tabela
// WinAnsi da fonte padrão; normaliza para equivalentes seguros antes de desenhar.
const _PDF_TEXTO_SUBSTITUICOES=[
  [new RegExp(String.fromCharCode(0x21B3),'g'),'-'],
  [/📞/gu,'Tel: '],
  [/📧/gu,'Email: '],
  [new RegExp(String.fromCharCode(0xA0),'g'),' '],
];
function _pdfSanitizarTexto(s){
  let out=String(s??'');
  for(const [re,rep] of _PDF_TEXTO_SUBSTITUICOES)out=out.replace(re,rep);
  return out;
}

function _pdfEstiloFonte(bold,italic){return bold&&italic?'bolditalic':bold?'bold':italic?'italic':'normal';}

function _pdfCheckPageBreak(doc,ctx,neededH=0){
  if(ctx.y+neededH>ctx.pageH-ctx.marginBottom){doc.addPage();ctx.y=ctx.marginTop;}
}

// Extrai "runs" de texto com estilo (negrito/itálico) preservando a árvore inline.
// Insere um espaço defensivo entre elementos adjacentes que não tenham espaço
// natural entre si (evita colar texto de tags vizinhas, ex.: <span>A</span><b>B</b>)
// e força quebra de linha após <small> (padrão "rótulo em cima do valor" dos templates).
function _pdfExtrairRuns(node,style={bold:false,italic:false}){
  const runs=[];
  node.childNodes.forEach(ch=>{
    if(ch.nodeType===3){
      if(ch.textContent)runs.push({text:ch.textContent,...style});
      return;
    }
    if(ch.nodeType!==1)return;
    const tag=ch.tagName.toLowerCase();
    if(tag==='script'||tag==='style')return;
    if(tag==='br'){runs.push({text:'\n',...style});return;}
    const nextStyle={bold:style.bold||tag==='strong'||tag==='b',italic:style.italic||tag==='em'||tag==='i'};
    const filhoRuns=_pdfExtrairRuns(ch,nextStyle);
    if(!filhoRuns.length)return;
    const ultimoAnterior=runs[runs.length-1];
    const precisaEspaco=ultimoAnterior&&!/[\s\n]$/.test(ultimoAnterior.text)&&!/^[\s\n]/.test(filhoRuns[0].text);
    if(precisaEspaco)runs.push({text:' ',...style});
    runs.push(...filhoRuns);
    if(tag==='small')runs.push({text:'\n',...style});
  });
  return runs;
}

function _pdfMedirRun(doc,run){
  doc.setFont('helvetica',_pdfEstiloFonte(run.bold,run.italic));
  doc.setFontSize(run.size);
  return doc.getTextWidth(run.text);
}

// Motor de fluxo de texto: quebra os runs em palavras, mede cada uma com a fonte/
// estilo corretos e desenha linha a linha, paginando automaticamente quando necessário.
function _pdfEscreverParagrafo(doc,ctx,runsInput,opts={}){
  const {indent=0,align='left',spacingBefore=0,spacingAfter=2,size=10,gap=1.28}=opts;
  if(!runsInput.length)return;
  const maxW=ctx.maxW-indent;
  const lineH=size*gap*0.352778;
  ctx.y+=spacingBefore;
  const tokens=[];
  runsInput.forEach(r=>{
    const texto=_pdfSanitizarTexto(r.text);
    const segmentos=texto.split('\n');
    segmentos.forEach((seg,i)=>{
      if(i>0)tokens.push({quebra:true});
      seg.split(/(\s+)/).filter(Boolean).forEach(p=>tokens.push({text:p,bold:r.bold,italic:r.italic,size:r.size||size}));
    });
  });
  let linha=[],larguraLinha=0;
  const desenharLinha=()=>{
    while(linha.length&&/^\s+$/.test(linha[linha.length-1].text))linha.pop();
    if(!linha.length){ctx.y+=lineH;return;}
    _pdfCheckPageBreak(doc,ctx,lineH);
    const larguraTotal=linha.reduce((s,w)=>s+_pdfMedirRun(doc,w),0);
    let x=ctx.marginLeft+indent;
    if(align==='right')x=ctx.marginLeft+ctx.maxW-larguraTotal;
    else if(align==='center')x=ctx.marginLeft+indent+(maxW-larguraTotal)/2;
    linha.forEach(w=>{
      doc.setFont('helvetica',_pdfEstiloFonte(w.bold,w.italic));
      doc.setFontSize(w.size);
      doc.text(w.text,x,ctx.y);
      x+=doc.getTextWidth(w.text);
      ctx.charCount=(ctx.charCount||0)+w.text.length;
    });
    ctx.y+=lineH;
    linha=[];larguraLinha=0;
  };
  tokens.forEach(tok=>{
    if(tok.quebra){desenharLinha();return;}
    if(/^\s+$/.test(tok.text)){
      if(!linha.length)return;
      const w=_pdfMedirRun(doc,tok);
      if(larguraLinha+w>maxW)desenharLinha();
      else{linha.push(tok);larguraLinha+=w;}
      return;
    }
    const w=_pdfMedirRun(doc,tok);
    if(larguraLinha+w>maxW&&linha.length)desenharLinha();
    linha.push(tok);larguraLinha+=w;
  });
  desenharLinha();
  ctx.y+=spacingAfter;
}

// Extrai o texto de uma célula de tabela com a mesma lógica de separação segura
// (quebra após blocos, espaço defensivo entre elementos inline adjacentes).
function _pdfTextoCelula(node){
  let out='';
  node.childNodes.forEach(ch=>{
    if(ch.nodeType===3){out+=ch.textContent;return;}
    if(ch.nodeType!==1)return;
    const tag=ch.tagName.toLowerCase();
    if(tag==='script'||tag==='style')return;
    if(tag==='br'){out+='\n';return;}
    const filho=_pdfTextoCelula(ch);
    if(!filho)return;
    const bloco=tag==='small'||tag==='div'||tag==='p';
    if(bloco){
      if(out&&!out.endsWith('\n'))out+='\n';
      out+=filho;
      if(!out.endsWith('\n'))out+='\n';
    }else{
      const precisaEspaco=out&&!/[\s\n]$/.test(out)&&!/^[\s\n]/.test(filho);
      out+=(precisaEspaco?' ':'')+filho;
    }
  });
  return out;
}

function _pdfAlinhamentoCelula(style){
  if(/text-align:\s*center/i.test(style||''))return'center';
  if(/text-align:\s*right/i.test(style||''))return'right';
  return'left';
}

function _pdfExtrairLinhaTabela(tr){
  return Array.from(tr.children).filter(c=>c.tagName==='TD'||c.tagName==='TH').map(td=>{
    const colSpan=parseInt(td.getAttribute('colspan')||'1',10);
    const texto=_pdfSanitizarTexto(_pdfTextoCelula(td).split('\n').map(s=>s.trim()).filter(Boolean).join('\n'));
    const cell={content:texto,styles:{halign:_pdfAlinhamentoCelula(td.getAttribute('style'))}};
    if(colSpan>1)cell.colSpan=colSpan;
    return cell;
  });
}

function _pdfDesenharTabela(doc,ctx,table){
  const thead=table.querySelector(':scope > thead');
  const tbody=table.querySelector(':scope > tbody');
  const tfoot=table.querySelector(':scope > tfoot');
  const head=thead?Array.from(thead.querySelectorAll(':scope > tr')).map(_pdfExtrairLinhaTabela):[];
  const corpo=tbody?Array.from(tbody.querySelectorAll(':scope > tr')):Array.from(table.querySelectorAll(':scope > tr'));
  const body=corpo.map(_pdfExtrairLinhaTabela);
  if(tfoot)body.push(...Array.from(tfoot.querySelectorAll(':scope > tr')).map(_pdfExtrairLinhaTabela));
  if(!head.length&&!body.length)return;
  _pdfCheckPageBreak(doc,ctx,14);
  doc.autoTable({
    head,body,startY:ctx.y,
    margin:{left:ctx.marginLeft,right:ctx.marginRight,bottom:ctx.marginBottom,top:ctx.marginTop},
    styles:{font:'helvetica',fontSize:8.5,cellPadding:2,textColor:30,lineColor:210,lineWidth:.1,valign:'middle'},
    headStyles:{fillColor:[13,53,84],textColor:255,fontStyle:'bold',fontSize:8.5},
    theme:'grid',
  });
  ctx.y=doc.lastAutoTable.finalY+4;
}

async function _pdfDesenharImagem(doc,ctx,img){
  let src=(img.getAttribute('src')||'').trim();
  if(!src)return;
  if(!src.startsWith('data:')){
    try{
      const r=await fetch(src,{cache:'force-cache'});
      if(!r.ok)throw new Error('imagem indisponível');
      const blob=await r.blob();
      src=await new Promise((res,rej)=>{const rd=new FileReader();rd.onload=e=>res(e.target.result);rd.onerror=rej;rd.readAsDataURL(blob);});
    }catch(e){return;}
  }
  const formato=(/data:image\/(png|jpe?g)/i.exec(src)?.[1]||'PNG').toUpperCase().replace('JPG','JPEG');
  let w=30,h=20;
  try{
    const dim=await new Promise((res,rej)=>{const im=new Image();im.onload=()=>res({w:im.naturalWidth,h:im.naturalHeight});im.onerror=rej;im.src=src;});
    if(dim.w&&dim.h){w=30;h=w*(dim.h/dim.w);if(h>22){h=22;w=h*(dim.w/dim.h);}}
  }catch(e){}
  _pdfCheckPageBreak(doc,ctx,h+3);
  try{doc.addImage(src,formato,ctx.marginLeft,ctx.y,w,h);}catch(e){}
  ctx.y+=h+4;
}

const _PDF_TAGS_BLOCO=new Set(['DIV','P','TABLE','UL','OL','SECTION','MAIN','H1','H2','H3','HEADER','FOOTER']);

async function _pdfRenderizarNos(doc,ctx,nodes,estado){
  for(const node of nodes)await _pdfRenderizarNo(doc,ctx,node,estado);
}

async function _pdfRenderizarNo(doc,ctx,node,estado){
  if(node.nodeType===3){
    const t=node.textContent.trim();
    if(t)_pdfEscreverParagrafo(doc,ctx,[{text:t}],{indent:estado.indent});
    return;
  }
  if(node.nodeType!==1)return;
  const tag=node.tagName.toLowerCase();
  if(tag==='style'||tag==='script'||tag==='head'||tag==='title')return;
  if(tag==='img'){await _pdfDesenharImagem(doc,ctx,node);return;}
  if(tag==='table'){
    const linhas=Array.from(node.querySelectorAll(':scope > tbody > tr, :scope > tr'));
    if(linhas.length===1){
      const celulas=Array.from(linhas[0].children);
      if(celulas.length===1&&celulas[0].querySelector('div,p,h1,h2,h3,table')){
        // tabela usada apenas como artifício de layout/paginação (ex.: molde de impressão);
        // não é dado tabular — desembrulha e renderiza o conteúdo interno normalmente.
        await _pdfRenderizarNos(doc,ctx,Array.from(celulas[0].childNodes),estado);
        return;
      }
    }
    _pdfDesenharTabela(doc,ctx,node);
    return;
  }
  if(tag==='h1'||tag==='h2'||tag==='h3'){
    const size=tag==='h1'?15:tag==='h2'?13:11.5;
    ctx.y+=tag==='h1'?4:3;
    _pdfCheckPageBreak(doc,ctx,size*0.4);
    _pdfEscreverParagrafo(doc,ctx,_pdfExtrairRuns(node,{bold:true}),{size,spacingAfter:3,align:tag==='h1'?'center':'left'});
    return;
  }
  if(tag==='ul'||tag==='ol'){
    let i=0;
    for(const li of Array.from(node.children)){
      if(li.tagName!=='LI')continue;
      i++;
      const marcador=tag==='ol'?`${i}.`:'-';
      _pdfEscreverParagrafo(doc,ctx,[{text:marcador+' '},..._pdfExtrairRuns(li)],{indent:estado.indent+5,spacingAfter:1.5});
    }
    ctx.y+=1.5;
    return;
  }
  if(_PDF_TAGS_BLOCO.has(node.tagName)){
    const temFilhoBloco=Array.from(node.children).some(c=>_PDF_TAGS_BLOCO.has(c.tagName));
    if(temFilhoBloco){
      await _pdfRenderizarNos(doc,ctx,Array.from(node.childNodes),estado);
      return;
    }
  }
  // folha: parágrafo inline (div/p sem filhos de bloco, ou elemento inline solto)
  const runs=_pdfExtrairRuns(node);
  if(runs.some(r=>r.text.trim()))_pdfEscreverParagrafo(doc,ctx,runs,{indent:estado.indent});
}

async function _pdfRenderizarHtml(doc,htmlString,opts={}){
  const dom=new DOMParser().parseFromString(htmlString,'text/html');
  const ctx={
    marginLeft:opts.marginLeft??15,marginRight:opts.marginRight??15,
    marginTop:opts.marginTop??15,marginBottom:opts.marginBottom??16,
    pageH:doc.internal.pageSize.getHeight(),
  };
  ctx.maxW=doc.internal.pageSize.getWidth()-ctx.marginLeft-ctx.marginRight;
  ctx.y=ctx.marginTop;
  ctx.charCount=0;
  doc.setFont('helvetica','normal');
  doc.setTextColor(20,20,20);
  await _pdfRenderizarNos(doc,ctx,Array.from(dom.body.childNodes),{indent:0});
  return ctx;
}

// Gera um PDF de texto real (base64) a partir de uma string HTML, usando o motor acima.
// Falha explicitamente se pouquíssimo texto foi desenhado (HTML vazio/quebrado),
// em vez de deixar passar um PDF em branco silenciosamente.
async function _pdfGerarBase64(htmlString,opts={}){
  await _carregarJsPDFTexto();
  const {jsPDF}=window.jspdf;
  const doc=new jsPDF({unit:'mm',format:'a4',orientation:'portrait'});
  const ctx=await _pdfRenderizarHtml(doc,htmlString,opts);
  if((ctx.charCount||0)<40)throw new Error('PDF saiu vazio/incompleto — nenhum texto foi desenhado.');
  const blob=doc.output('blob');
  return await new Promise((resolve,reject)=>{
    const reader=new FileReader();
    reader.onload=()=>resolve(reader.result.split(',')[1]);
    reader.onerror=reject;
    reader.readAsDataURL(blob);
  });
}

async function _carregarPdfLib(){
  if(window.PDFLib)return;
  await new Promise((resolve,reject)=>{
    const s=document.createElement('script');
    s.src='https://cdnjs.cloudflare.com/ajax/libs/pdf-lib/1.17.1/pdf-lib.min.js';
    s.onload=resolve;s.onerror=()=>reject(new Error('Falha ao carregar pdf-lib.js'));
    document.head.appendChild(s);
  });
}
// Combina PDFs (base64) na ordem informada em um único PDF (base64).
// Usado para compor o documento de assinatura como Contrato + Orçamento.
async function _mergePdfsBase64(base64List){
  await _carregarPdfLib();
  const {PDFDocument}=window.PDFLib;
  const merged=await PDFDocument.create();
  for(const b64 of base64List){
    if(!b64)continue;
    const bin=atob(b64);
    const bytes=new Uint8Array(bin.length);
    for(let i=0;i<bin.length;i++)bytes[i]=bin.charCodeAt(i);
    const src=await PDFDocument.load(bytes);
    const pages=await merged.copyPages(src,src.getPageIndices());
    pages.forEach(pg=>merged.addPage(pg));
  }
  const mergedBytes=await merged.save();
  let binary='';
  const chunk=0x8000;
  for(let i=0;i<mergedBytes.length;i+=chunk){
    binary+=String.fromCharCode.apply(null,mergedBytes.subarray(i,i+chunk));
  }
  return btoa(binary);
}

